import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Schema para validação
const wppServiceSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  url: z.string(),
  api_token: z.string(),
  max_sessions: z.number().nullable(),
  current_sessions: z.number().nullable(),
  is_shared: z.boolean().nullable(),
  status: z.string().nullable(),
  last_heartbeat: z.string().nullable(),
  region: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type WppService = z.infer<typeof wppServiceSchema>;

// Buscar todos os serviços
async function fetchWppServices(): Promise<WppService[]> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .order('nome');

  if (error) throw error;

  const parsed = z.array(wppServiceSchema).safeParse(data);
  if (!parsed.success) {
    console.error('Invalid wpp_services data:', parsed.error.flatten());
    throw new Error('Dados inválidos dos serviços WPP');
  }

  return parsed.data;
}

// Buscar servidor disponível para nova sessão individual
async function fetchAvailableServer(): Promise<WppService | null> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('is_shared', false)
    .eq('status', 'active')
    .order('current_sessions', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  // Verificar se tem vagas
  const maxSessions = data.max_sessions ?? 3;
  const currentSessions = data.current_sessions ?? 0;
  
  if (currentSessions >= maxSessions) {
    return null; // Sem vagas
  }

  const parsed = wppServiceSchema.safeParse(data);
  if (!parsed.success) {
    console.error('Invalid wpp_service data:', parsed.error.flatten());
    return null;
  }

  return parsed.data;
}

// Buscar servidor compartilhado
async function fetchSharedServer(): Promise<WppService | null> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('is_shared', true)
    .eq('status', 'active')
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const parsed = wppServiceSchema.safeParse(data);
  if (!parsed.success) return null;

  return parsed.data;
}

// Buscar servidor por ID
async function fetchServerById(serverId: string): Promise<WppService | null> {
  const { data, error } = await supabase
    .from('wpp_services')
    .select('*')
    .eq('id', serverId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const parsed = wppServiceSchema.safeParse(data);
  if (!parsed.success) return null;

  return parsed.data;
}

export function useWppServices() {
  const queryClient = useQueryClient();

  // Query para listar todos os serviços
  const servicesQuery = useQuery({
    queryKey: ['wppServices'],
    queryFn: fetchWppServices,
    staleTime: 1000 * 30, // 30 segundos
  });

  // Query para servidor disponível (individual)
  const availableServerQuery = useQuery({
    queryKey: ['wppServices', 'available'],
    queryFn: fetchAvailableServer,
    staleTime: 1000 * 10,
  });

  // Query para servidor compartilhado
  const sharedServerQuery = useQuery({
    queryKey: ['wppServices', 'shared'],
    queryFn: fetchSharedServer,
    staleTime: 1000 * 30,
  });

  // Mutation para atualizar servidor
  const updateServerMutation = useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<Pick<WppService, 'url' | 'api_token' | 'max_sessions' | 'status' | 'region'>> 
    }) => {
      const { data, error } = await supabase
        .from('wpp_services')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wppServices'] });
      toast.success('Servidor atualizado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao atualizar servidor: ${error.message}`);
    },
  });

  // Mutation para criar servidor
  const createServerMutation = useMutation({
    mutationFn: async (newServer: {
      nome: string;
      url: string;
      api_token: string;
      max_sessions?: number;
      is_shared?: boolean;
      region?: string;
    }) => {
      const { data, error } = await supabase
        .from('wpp_services')
        .insert([newServer])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wppServices'] });
      toast.success('Servidor criado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao criar servidor: ${error.message}`);
    },
  });

  // Mutation para deletar servidor
  const deleteServerMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('wpp_services')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wppServices'] });
      toast.success('Servidor removido com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao remover servidor: ${error.message}`);
    },
  });

  // Mutation para incrementar sessões de um servidor
  const incrementSessionsMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const { data: server } = await supabase
        .from('wpp_services')
        .select('current_sessions')
        .eq('id', serverId)
        .single();

      if (server) {
        const { data, error } = await supabase
          .from('wpp_services')
          .update({ current_sessions: (server.current_sessions ?? 0) + 1 })
          .eq('id', serverId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wppServices'] });
    },
  });

  // Mutation para decrementar sessões de um servidor
  const decrementSessionsMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const { data: server } = await supabase
        .from('wpp_services')
        .select('current_sessions')
        .eq('id', serverId)
        .single();

      if (server) {
        const newCount = Math.max(0, (server.current_sessions ?? 0) - 1);
        await supabase
          .from('wpp_services')
          .update({ current_sessions: newCount })
          .eq('id', serverId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wppServices'] });
    },
  });

  return {
    // Queries
    services: servicesQuery.data ?? [],
    servicesLoading: servicesQuery.isLoading,
    servicesError: servicesQuery.error,
    
    availableServer: availableServerQuery.data ?? null,
    availableServerLoading: availableServerQuery.isLoading,
    
    sharedServer: sharedServerQuery.data ?? null,
    sharedServerLoading: sharedServerQuery.isLoading,

    // Mutations
    updateServer: updateServerMutation.mutate,
    updateServerAsync: updateServerMutation.mutateAsync,
    isUpdating: updateServerMutation.isPending,

    createServer: createServerMutation.mutate,
    createServerAsync: createServerMutation.mutateAsync,
    isCreating: createServerMutation.isPending,

    deleteServer: deleteServerMutation.mutate,
    deleteServerAsync: deleteServerMutation.mutateAsync,
    isDeleting: deleteServerMutation.isPending,

    incrementSessions: incrementSessionsMutation.mutate,
    decrementSessions: decrementSessionsMutation.mutate,

    // Helpers
    fetchServerById,
    refetch: () => {
      servicesQuery.refetch();
      availableServerQuery.refetch();
      sharedServerQuery.refetch();
    },
  };
}
